import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';

import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { escapeHtml } from '../../../core/validators/no-special-chars.validator';
import { decodeAssetType, decodeSubDomain } from '../../models/document-asset-labels';
import {
  DocumentAssetStatus,
  DocumentAssetSummary,
  DocumentAssetType,
} from '../../models/document-asset.model';
import { DocumentArticleV2Service } from '../../services/document-article-v2.service';
import { ColumnConfig } from '../dashboard/table-config';
import {
  DocumentAssetRegisteredEvent,
  DocumentAssetUploadWizardComponent,
} from '../document-asset-upload-wizard/document-asset-upload-wizard.component';

interface ArticleRow {
  assetId: number;
  id: number;
  businessKey: string;
  name: string;
  assetType: DocumentAssetType | string;
  subDomainType: string;
  versionLabel: string;
  status: DocumentAssetStatus | string;
  lastModified: string;
  originalFileName: string;
  raw: DocumentAssetSummary;
}

@Component({
  selector: 'app-document-article-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RegistryTableComponent,
    Toast,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    DocumentAssetUploadWizardComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './document-article-list.component.html',
  styleUrls: ['./document-article-list.component.scss'],
})
export class DocumentArticleListComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly articleService = inject(DocumentArticleV2Service);
  private readonly navigationContext = inject(NavigationContextService);
  private readonly titleService = inject(Title);

  readonly articlesData = signal<ArticleRow[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly breadcrumbItems = signal<MenuItem[]>([]);
  readonly wizardVisible = signal<boolean>(false);
  readonly wizardMode = signal<'create' | 'edit'>('create');
  readonly editingAsset = signal<DocumentAssetSummary | null>(null);
  readonly showInactive = signal<boolean>(false);
  readonly emptyMessage = signal<string>(
    'Nessun articolo riusabile registrato. Carica il tuo primo articolo PDF o DOCX.',
  );

  readonly columns: ColumnConfig[] = [
    { field: 'name', title: 'Nome Articolo', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    {
      field: 'assetType',
      title: 'Tipologia',
      type: 'tag',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca tipologia',
    },
    { field: 'subDomainType', title: 'Categoria', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca categoria' },
    { field: 'versionLabel', title: 'Versione', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Bozza', value: 'DRAFT' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'Ritirato', value: 'RETIRED' },
      ],
    },
    { field: 'lastModified', title: 'Ultima Modifica', type: 'text', sortable: true, filterType: 'date' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  ngOnInit(): void {
    this.titleService.setTitle('Articoli Riusabili - PalEasy');
    this.breadcrumbItems.set([{ label: 'Articoli Riusabili' }]);
    this.loadArticles();
  }

  loadArticles(): void {
    this.isLoading.set(true);
    const domainType = this.currentDomainType();
    this.articleService.list({ domainType, includeInactive: this.showInactive() }).subscribe({
      next: (response) => {
        const items = response?.data ?? [];
        this.articlesData.set(items.map((item) => this.toRow(item)));
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading V2 articles:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare gli articoli riusabili',
        });
        this.articlesData.set([]);
        this.isLoading.set(false);
      },
    });
  }

  createNewArticle(): void {
    this.wizardMode.set('create');
    this.editingAsset.set(null);
    this.wizardVisible.set(true);
  }

  onToggleShowInactive(value: boolean): void {
    this.showInactive.set(value);
    this.loadArticles();
  }

  onEditClick(row: ArticleRow): void {
    this.wizardMode.set('edit');
    this.editingAsset.set(row.raw);
    this.wizardVisible.set(true);
  }

  onWizardRegistered(event: DocumentAssetRegisteredEvent): void {
    this.loadArticles();
    this.messageService.add({
      severity: 'success',
      summary: 'Articolo registrato',
      detail: `${event.businessKey} v${event.version} \u2014 stato ${event.status.toLowerCase()}.`,
      life: 4000,
    });
  }

  onDownloadClick(row: ArticleRow): void {
    this.articleService.download(row.assetId).subscribe({
      next: (blob) => this.triggerDownload(blob, row.originalFileName || `${row.businessKey}.bin`),
      error: (err) => {
        console.error('Error downloading article:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare l\u2019articolo',
        });
      },
    });
  }

  onDeleteClick(row: ArticleRow): void {
    this.articleService.references(row.assetId).subscribe({
      next: (response) => this.confirmDeleteWithReferences(row, response?.data),
      error: (err) => {
        console.error('Error loading references:', err);
        this.confirmDeleteWithReferences(row, undefined);
      },
    });
  }

  onClearFilters(): void {
    this.showInactive.set(false);
    this.loadArticles();
  }

  onExport(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Esportazione',
      detail: 'La funzione di esportazione sarà disponibile a breve.',
      life: 3000,
    });
  }

  private confirmDeleteWithReferences(
    row: ArticleRow,
    references:
      | { activeReferenceCount?: number; retiredReferenceCount?: number }
      | undefined,
  ): void {
    const activeRefs = references?.activeReferenceCount ?? 0;
    const requiresForce = activeRefs > 0;
    const message = requiresForce
      ? `L\u2019articolo <strong>${escapeHtml(row.name || row.businessKey)}</strong> è utilizzato da <strong>${activeRefs}</strong> template attivo/i. Confermando verrà ritirato ma i binding esistenti resteranno intatti nelle versioni già in uso. Procedere?`
      : `Sei sicuro di voler ritirare l\u2019articolo <strong>${escapeHtml(row.name || row.businessKey)}</strong>? L\u2019operazione è reversibile creando una nuova versione.`;

    this.confirmationService.confirm({
      header: requiresForce ? 'Conferma Eliminazione Forzata' : 'Conferma Eliminazione',
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: requiresForce ? 'Sì, ritira comunque' : 'Sì, ritira',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.performDelete(row, requiresForce),
    });
  }

  private performDelete(row: ArticleRow, force: boolean): void {
    this.articleService.delete(row.assetId, force).subscribe({
      next: (response) => {
        if (response?.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Articolo ritirato',
            detail: `L\u2019articolo "${row.name || row.businessKey}" è stato ritirato correttamente.`,
            life: 3000,
          });
          this.loadArticles();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: response?.message ?? 'Impossibile ritirare l\u2019articolo',
          });
        }
      },
      error: (err) => {
        console.error('Error deleting article:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile ritirare l\u2019articolo',
        });
      },
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private toRow(item: DocumentAssetSummary): ArticleRow {
    return {
      assetId: item.assetId,
      id: item.assetId,
      businessKey: item.businessKey,
      name: item.name ?? '',
      assetType: decodeAssetType(item.assetType ?? 'ARTICLE'),
      subDomainType: decodeSubDomain(item.subDomainType),
      versionLabel: `v${item.version}`,
      status: item.status ?? 'DRAFT',
      lastModified: this.formatDate(item.updatedAt ?? item.createdAt),
      originalFileName: item.originalFileName ?? '',
      raw: item,
    };
  }

  private formatDate(value?: string): string {
    if (!value) return '-';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('it-IT');
  }

  currentDomainType(): 'SUPPLIERS' | 'PROFESSIONALS' {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  private domainPrefix(): string {
    return this.navigationContext.getDomainPrefix();
  }
}
